import React, {useMemo} from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import throttle from 'lodash.throttle'
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import {selectedCompanyStore} from "../../../store/SelectedCompany";
import {invoiceDataStore} from "../../../store/InvoiceDataStore";
import {CustomerDto} from "../../../model/customer/Customer";
import {createFilterOptions} from '@mui/material/useAutocomplete';
import EditCustomerDialog from "../../customer/EditCustomerDialog";
import TextFieldWithValidation from "../../../components/input/TextFieldWithValidation";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import {salesInvoiceApi} from "../../../backend/SalesInvoiceApi";
import {Document, Page} from 'react-pdf/dist/esm/entry.webpack';
import {SalesInvoiceLineDto} from "../../../model/invoice/sales/SalesInvoiceLineDto";
import NumberFormat from 'react-number-format';
import DeleteIcon from "@mui/icons-material/Delete";
import {calculateLines, InvoiceCalculationResult} from "../../../utils/InvoiceCalculator";
import {CreateSalesInvoiceDto} from "../../../model/invoice/sales/CreateSalesInvoiceDto";
import LoadingButton from "../../../components/button/LoadingButton";
import {routes} from "../../../main/Routes";
import {customersStore} from "../../../store/CustomersStore";
import {InvoiceTotals} from "../../../components/invoice/InvoiceTotals";
import ButtonWithConfirmation from "../../../components/button/ButtonWithConfirmation";
import {observer} from "mobx-react";
import {invoiceWizardState} from "../../../store/InvoiceWizardState";
import {NavigateFunction} from "react-router/lib/hooks";
import {DatePicker} from "../../../components/picker/DatePicker";
import {Trans, Translation, useTranslation, withTranslation} from "react-i18next";
import {useTranslateCommon} from "../../../utils/TranslationUtils";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CurrencySelector from "../../../components/CurrencySelector";

const filter = createFilterOptions();

interface IProps {
    navigate: NavigateFunction;
}

interface IState {
    loading: boolean,
    activeStep: number,

    showAddCustomerDialog: boolean,
    expectedNextInvoiceNumber?: string,

    customInvoiceValid?: boolean | 'pending',

    pdfPreview?: string;
}

const linesCellStyles = {
    padding: "4px",
}

@observer
export class GenerateNewInvoiceWizard extends React.Component<IProps, IState> {
    private NEW_CUSTOMER = 'NEW_CUSTOMER_ID'

    constructor(props: IProps) {
        super(props);
        this.state = {
            ...this.loadingState(),
            activeStep: 0,
        };
    }

    private loadingState() {
        return {
            showAddCustomerDialog: false,
            loading: true,
        };
    }

    async componentDidMount() {
        if (selectedCompanyStore.isSelected) {
            await invoiceDataStore.refresh();
            await this.refreshCustomers();
        }
    }

    private refreshCustomers = async () => {
        await customersStore.refresh();
        this.setState({
            loading: false
        })
    }

    private convertToOptionForSelect = (option: CustomerDto) => {
        const firstLetter = option.name!![0].toUpperCase();
        return {
            firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
            ...option,
        };
    };


    render() {
        const {loading, activeStep} = this.state;

        return (
                <>
                    <LoaderWithCompanySelected loading={loading}>
                        <Stepper orientation="vertical" activeStep={activeStep}>
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">
                                        <Trans i18nKey="invoice_wizard.customer_step.header">Customer</Trans>
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    {this.renderSelectCustomerStep()}
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">
                                        <Trans i18nKey="invoice_wizard.invoice_content_step.header">Invoice
                                            content</Trans>
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    {this.renderInvoiceContentStep()}
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel><Typography variant="h6"><Trans
                                        i18nKey="common.preview">Preview</Trans></Typography></StepLabel>
                                <StepContent>
                                    {this.renderReviewContent()}
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel><Typography variant="h6"><Trans i18nKey="invoice_wizard.new_invoice_header">New
                                    invoice</Trans></Typography></StepLabel>
                                <StepContent>
                                </StepContent>
                            </Step>
                        </Stepper>
                    </LoaderWithCompanySelected>
                </>
        );
    }

    private renderReviewContent() {
        return (
                <>
                    <Grid style={{width: "610px", border: "1px solid #efefef", padding: "5px"}}>
                        {this.state.pdfPreview && <>
                            <Document file={`data:application/pdf;base64,${this.state.pdfPreview}`}>
                                <Page pageIndex={0}/>
                            </Document>
                        </>}
                        {!this.state.pdfPreview && <CircularProgress/>}
                    </Grid>
                    {this.divider()}
                    <div>
                        <div>
                            <Button disabled={!this.state.pdfPreview} onClick={this.handleBack}><Trans
                                    i18nKey="common.back">Back</Trans></Button>
                            <LoadingButton variant="contained" disabled={!this.state.pdfPreview}
                                           onClick={this.createInvoice} color="secondary">
                                <Trans i18nKey="invoice_wizard.create_invoice">Create invoice</Trans>
                            </LoadingButton>
                        </div>
                    </div>
                </>

        )
    }

    private renderSelectCustomerStep = () => {
        const customers = customersStore.customers;
        const {showAddCustomerDialog} = this.state
        const selectedCustomerId = invoiceWizardState.selectedCustomerId
        const customersForSelect = customers
                .map(this.convertToOptionForSelect)
                .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
        return <>
            <Typography variant="subtitle2">
                <Trans i18nKey="invoice_wizard.customer_step.subtitle">Please select or create a customer you are
                    invoicing</Trans>
            </Typography>
            <Grid container>
                <Grid item xs={10}>
                    <Translation>
                        {t => <Autocomplete
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={selectedCustomerId ? customersForSelect.find(it => it.id === selectedCustomerId) : null}
                                options={customersForSelect}
                                groupBy={(option) => option.firstLetter}
                                getOptionLabel={(option) => option.name!!}
                                fullWidth={true}
                                renderInput={(params) =>
                                        <TextFieldWithValidation{...params}
                                                                label={t("invoice_wizard.customer_step.select_customer", "Select a customer")}
                                                                error={selectedCustomerId === undefined}
                                        />
                                }
                                onChange={this.handleSelectedCustomerChanged}
                                // @ts-ignore
                                filterOptions={(options, params) => {
                                    // @ts-ignore
                                    const filtered = filter(options, params);

                                    if (params.inputValue !== '') {
                                        filtered.push({
                                            id: this.NEW_CUSTOMER,
                                            name: t("invoice_wizard.customer_step.add_new_customer_question", `Would you like create a new customer?`),
                                        });
                                    }
                                    return filtered;
                                }}
                        />
                        }
                    </Translation>

                </Grid>
                <Grid xs={2} item container alignItems="flex-end">
                    <Button variant="contained" color="primary" startIcon={<AddIcon/>}
                            onClick={() => this.setState({showAddCustomerDialog: true})}>
                        <Trans i18nKey="invoice_wizard.customer_step.new_customer">New</Trans>
                    </Button>
                </Grid>

            </Grid>
            {showAddCustomerDialog &&
                    <EditCustomerDialog open={showAddCustomerDialog}
                                        onSave={this.handleNewCustomerAdded}
                                        onCancel={this.handleCloseAddCustomerDialog}/>
            }
            {this.divider()}
            <div>
                <Grid container spacing={1}>
                    <Grid item>
                        <Tooltip title={<Trans i18nKey="invoice_wizard.customer_step.clear_data">Clears previously
                            entered data, including invoice lines</Trans>}>
                            <Button variant="contained" color="secondary" disabled={selectedCustomerId === undefined}
                                    onClick={() => invoiceWizardState.resetInvoiceWizardState()}>
                                <Trans i18nKey="common.clear">Clear</Trans>
                            </Button>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <LoadingButton variant="contained" color="primary" disabled={selectedCustomerId === undefined}
                                       onClick={this.handleToInvoiceContent}>
                            <Trans i18nKey="common.next">Next</Trans>
                        </LoadingButton>
                    </Grid>
                </Grid>
            </div>
        </>;
    }

    private createInvoice = async () => {
        const createInvoiceRq = this.assembleCreateInvoiceDto();
        const newInvoice = await salesInvoiceApi.createInvoice(createInvoiceRq);
        invoiceWizardState.resetInvoiceWizardState()
        this.props.navigate(routes.salesInvoice.replace(':id', newInvoice.id))
    }

    private renderInvoiceContentStep = () => {
        const {expectedNextInvoiceNumber, customInvoiceValid} = this.state;
        const {selectedCustomerId, invoiceLines} = invoiceWizardState

        const customer = customersStore.find(selectedCustomerId);
        if (customer) {
            const invoiceData = invoiceWizardState;
            const {customInvoiceNumber} = invoiceWizardState;
            const {invoiceDate, dueDate, selectedCurrency} = this.withCustomerDefaults(invoiceWizardState, customer);

            const articlesSelectable = Object.values(invoiceDataStore.articles).filter(it => it.selectableByUser).map(it => it.code)
            const calculationResult = calculateLines(invoiceDataStore.taxes, invoiceLines);
            const nextDisabled = selectedCustomerId === undefined || (!!customInvoiceNumber && customInvoiceValid !== true)
                    || invoiceLines.length === 0 || Boolean(invoiceLines.find(this.isInvoiceLineDescriptionTooLong))
                    || this.isInvoiceCommentTooLong()
                    || invoiceLines.find(it => !articlesSelectable.includes(it.item.code)) != undefined;

            const tableColumns = this.createTableColumns(calculationResult);
            const minDueDate = invoiceDate?.clone().startOf('day').add(1, 'day');

            const has22PercentTaxAfterJune25 = invoiceDate.year() > 2024
                    && invoiceDate.month() >= 6
                    && invoiceLines.find(it => it.taxCode === "22%");

            return <>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <Grid container spacing={0.4}>
                                <Grid item>
                                    <Trans i18nKey="invoice_wizard.invoice_content_step.invoice_to">Invoice to</Trans>
                                </Grid>
                                <Grid item>
                                    <b>{customer.name}</b>
                                </Grid>
                            </Grid>
                        </Typography>
                    </Grid>

                    <Grid item container xs={12} spacing={2}>
                        <Grid item>
                            <DatePicker
                                    label={<Trans i18nKey="invoice_wizard.invoice_content_step.date">Invoice
                                        date</Trans>}
                                    value={invoiceDate}
                                    onChange={(date) => invoiceWizardState.invoiceDate = date!!}
                            />
                        </Grid>
                        <Grid item>
                            <DatePicker
                                    label={<Trans i18nKey="invoice_wizard.invoice_content_step.due">Due date</Trans>}
                                    value={dueDate}
                                    onChange={(date) => invoiceWizardState.dueDate = date!!}
                                    minDate={minDueDate}
                                    error={dueDate.isBefore(minDueDate) ? "Due date can't be less than invoice date" : ""}
                            />
                        </Grid>
                        <Grid item>
                            <TextFieldWithValidation
                                    label={<Trans i18nKey="invoice_wizard.invoice_content_step.inv_number">Invoice
                                        number</Trans>}
                                    placeholder={expectedNextInvoiceNumber}
                                    value={customInvoiceNumber}
                                    onChange={async e => {
                                        invoiceWizardState.customInvoiceNumber = e.target.value.toUpperCase();
                                        await this.doValidateInvoiceNumber();
                                    }}
                                    hasError={customInvoiceValid !== undefined && !customInvoiceValid}
                                    errorText={<Trans i18nKey="invoice_wizard.invoice_content_step.inv_number_was_used">Was
                                        used already</Trans>}
                                    InputProps={{
                                        endAdornment: (
                                                <InputAdornment position="end">
                                                    {customInvoiceValid === 'pending' &&
                                                            <CircularProgress size="1rem"/>}
                                                </InputAdornment>
                                        ),
                                    }}
                            />
                        </Grid>
                        <Grid item>
                            <CurrencySelector
                                    label={<Trans
                                            i18nKey="invoice_wizard.invoice_content_step.currency">Currency</Trans>}
                                    onChange={currency => invoiceWizardState.selectedCurrency = currency}
                                    value={selectedCurrency}
                            />
                        </Grid>
                    </Grid>
                    {has22PercentTaxAfterJune25 && <Grid container spacing={2}>
                        <Grid item xs={12} style={{marginTop: "0.5rem", marginLeft: "6px"}}>
                            <Alert severity="error">
                                <Trans i18nKey="invoice_wizard.invoice_content_step.tax_rate_warning">
                                    You have selected a VAT rate of 22%, although the invoice date is after 1 Jul
                                    2025.<br/>
                                    As of July 1, 2025, the VAT rate in Estonia is 24%.<br/>
                                    If you have any questions regarding this, please contact our support.<br/>
                                </Trans>
                            </Alert>
                        </Grid>
                    </Grid>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{marginTop: "0.5rem", marginLeft: "6px"}}>
                            <Table size={"small"}>
                                <TableHead>
                                    <TableRow>
                                        {
                                            tableColumns.map((it, index) =>
                                                    <TableCell key={index} style={it.headerStyle}>{it.title}</TableCell>
                                            )
                                        }
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoiceLines.map((it, rowIdx) => {
                                        return <TableRow key={rowIdx}>
                                            {tableColumns.map((col, colIdx) => <TableCell
                                                    key={colIdx} style={col.cellStyle}>{col.render(it)}</TableCell>)}
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table>
                            <Button color="primary" fullWidth variant={"outlined"} style={{marginTop: "0.2rem"}}
                                    onClick={this.addInvoiceLine}>
                                <Trans i18nKey="invoice_wizard.invoice_content_step.new_line">New Line</Trans>
                            </Button>
                        </Grid>
                        <Grid xs={12} container item style={{marginTop: "0.5rem", marginLeft: "6px"}}>
                            <Grid item xs={7}>
                                <Translation>{t => <TextFieldWithValidation
                                        id="comment"
                                        label={t("invoice_wizard.invoice_content_step.comment", "Invoice comment")}
                                        placeholder={t("invoice_wizard.invoice_content_step.comment_placeholder", "Please leave any comment related to the invoice")}
                                        multiline
                                        value={invoiceWizardState.comment}
                                        fullWidth
                                        rows={3}
                                        onChange={e => invoiceWizardState.comment = e.target.value}
                                        hasError={this.isInvoiceCommentTooLong()}
                                        errorText={t("invoice_wizard.invoice_content_step.comment_too_long", "Comment is too long")}
                                />}
                                </Translation>
                            </Grid>
                            <Grid item xs={1}>
                            </Grid>
                            <Grid item xs={4}>
                                <InvoiceTotals calculationResult={calculationResult}
                                               currency={invoiceData.selectedCurrency ?? customer.defaultCurrency!!}/>
                            </Grid>
                        </Grid>

                    </Grid>
                    <Grid item xs={12}>
                    </Grid>
                </Grid>
                {this.divider()}
                <div>
                    <Grid container spacing={1}>
                        <Grid item>
                            <Button onClick={this.handleBack}><Trans i18nKey="common.back">Back</Trans></Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="primary" disabled={nextDisabled}
                                    onClick={this.handleToPreview}><Trans
                                    i18nKey="common.preview">Preview</Trans></Button>
                        </Grid>
                        <Grid item>
                            <ButtonWithConfirmation variant="contained" disabled={nextDisabled} color="secondary"
                                                    onClick={this.createInvoice}>
                                <Trans i18nKey="invoice_wizard.invoice_content_step.skip_preview">Skip preview & create
                                    invoice</Trans>
                            </ButtonWithConfirmation>
                        </Grid>
                    </Grid>
                </div>
            </>;
        } else {
            return <></>
        }
    }

    private isInvoiceCommentTooLong() {
        return (invoiceWizardState.comment?.length ?? 0) > 4000;
    }

    private doValidateInvoiceNumber = async () => {
        await this.setState({
                    customInvoiceValid: 'pending'
                }
        )
        this.validateInvoiceNumberDelayed();
    }

    private createTableColumns(calculationResult: InvoiceCalculationResult) {
        return [
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.item_service">Item/Service</Trans>,
                cellStyle: {
                    ...linesCellStyles,
                    width: "10%",
                    paddingLeft: "15px"
                },
                headerStyle: {
                    ...linesCellStyles,
                    width: "10%",
                    paddingLeft: "15px"
                },
                render: (it: SalesInvoiceLineDto) => <ArticleSelector row={it} changeArticle={this.changeArticle}/>,
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.description">Description</Trans>,
                cellStyle: {
                    ...linesCellStyles,
                    width: "20%",
                },
                headerStyle: {
                    ...linesCellStyles,
                    width: "20%",
                },
                render: (row: SalesInvoiceLineDto) => {
                    return (
                            <TextFieldWithValidation fullWidth
                                                     value={row.item?.description ?? ""}
                                                     onChange={(e) => this.changeRowDescription(row, e.target.value)}
                                                     hasError={this.isInvoiceLineDescriptionTooLong(row)}
                                                     multiline
                                                     errorText={<Trans
                                                             i18nKey="invoice_wizard.invoice_content_step.lines.description_too_long">Description
                                                         is too long</Trans>}
                            />
                    );
                }
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.quantity">Quantity</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => {
                    return <NumberFormat
                            style={{width: "100%"}}
                            customInput={TextFieldWithValidation}
                            value={row.quantity!!}
                            decimalScale={3}
                            onValueChange={(values) => {
                                this.changeRowQuantity(row, values.floatValue)
                            }}
                            isNumericString
                    />
                }
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.unit">Unit</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => <UomSelect row={row} changeUOM={this.changeUOM}/>
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.price">Price</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => {
                    return <>
                        <NumberFormat
                                style={{width: "100%"}}
                                customInput={TextFieldWithValidation}
                                thousandSeparator={'\''}
                                value={row.price!!}
                                decimalScale={2}
                                onValueChange={(values) => {
                                    this.changeRowPrice(row, values.floatValue)
                                }}
                                isNumericString
                        />
                    </>
                }
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.disc_pct">Disc. %</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => {
                    return <NumberFormat
                            style={{width: "100%"}}
                            customInput={TextFieldWithValidation}
                            value={row.discountPct!!}
                            allowNegative={false}
                            decimalScale={0}
                            onValueChange={(values) => {
                                this.changeRowDiscount(row, values.floatValue)
                            }}
                            isNumericString
                    />
                }
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.total_no_vat">Total Excl. VAT</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => {
                    return calculationResult.byLine(row).totalExclVat.toFixed(2)
                }
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.vat">VAT</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: this.renderVat
            },
            {
                title: <Trans i18nKey="invoice_wizard.invoice_content_step.lines.actions">Actions</Trans>,
                cellStyle: linesCellStyles,
                headerStyle: linesCellStyles,
                render: (row: SalesInvoiceLineDto) => {
                    return (
                            <div style={{width: "5rem"}}>
                                <IconButton
                                        color="secondary"
                                        onClick={() => {
                                            const salesInvoiceLineDtos = invoiceWizardState.invoiceLines;
                                            salesInvoiceLineDtos.remove(row);
                                        }}
                                        size="medium">
                                    <DeleteIcon/>
                                </IconButton>
                                <IconButton
                                        color="primary"
                                        onClick={() => {
                                            const index = invoiceWizardState.invoiceLines.indexOf(row);
                                            invoiceWizardState.invoiceLines.splice(index, 0, row)
                                        }}
                                        size="medium">
                                    <FileCopyIcon/>
                                </IconButton>
                            </div>
                    );
                }
            }
        ];
    }

    private isInvoiceLineDescriptionTooLong(row: SalesInvoiceLineDto) {
        return (row.item?.description?.length ?? 0) > 150;
    }

    private withCustomerDefaults = (invoiceData: {
        invoiceDate?: moment.Moment;
        dueDate?: moment.Moment;
        customInvoiceNumber?: string;
        selectedCurrency?: string
    }, customer: CustomerDto) => {
        let {invoiceDate, dueDate, selectedCurrency} = invoiceData;
        if (!invoiceDate) {
            invoiceDate = moment()
        }
        if (!dueDate) {
            dueDate = invoiceDate.clone().add(customer.paymentDeadLine, 'days')
        }
        if (!selectedCurrency) {
            selectedCurrency = customer.defaultCurrency;
        }
        return {invoiceDate, dueDate, selectedCurrency};
    }

    private handleSelectedCustomerChanged = (event: any, value: CustomerDto | null) => {
        const selectedCustomerId = value?.id;
        if (selectedCustomerId === this.NEW_CUSTOMER) {
            this.setState({
                showAddCustomerDialog: true,
            })
            invoiceWizardState.selectedCustomerId = undefined;
        } else {
            invoiceWizardState.selectedCustomerId = selectedCustomerId;
        }
    }

    private handleNewCustomerAdded = async (changeCustomer: CustomerDto) => {
        this.setState({
            ...this.loadingState(),
        })
        invoiceWizardState.selectedCustomerId = changeCustomer.id;
        await this.refreshCustomers();
    }

    private handleCloseAddCustomerDialog = () => {
        this.setState({
            showAddCustomerDialog: false
        })
    }

    private divider = () => {
        return <Divider style={{marginBottom: "0.25rem", marginTop: "0.25rem"}}/>
    }

    private handleNext = () => {
        this.setState({activeStep: this.state.activeStep + 1});

    };

    private handleToInvoiceContent = async () => {
        const nextInvoiceNumber = await salesInvoiceApi.getNextInvoiceNumber();
        this.setState({
            expectedNextInvoiceNumber: nextInvoiceNumber.nextNumber
        })
        if (invoiceWizardState.invoiceLines.length === 0) {
            this.addInvoiceLine()
        }
        if (invoiceWizardState.customInvoiceNumber) {
            await this.doValidateInvoiceNumber();
        }
        this.handleNext();
    }

    private handleToPreview = async () => {
        this.handleNext();
        this.setState({pdfPreview: undefined})
        await this.fetchPdf();
    }

    private handleBack = () => {
        this.setState({activeStep: this.state.activeStep - 1});
    };

    private addInvoiceLine = () => {
        const newItem = this.buildNextItem();
        invoiceWizardState.invoiceLines.push(newItem)
    }

    private buildNextItem = () => {
        const length = invoiceWizardState.invoiceLines.length;
        if (length > 0) {
            const lastItem = invoiceWizardState.invoiceLines[length - 1];
            return {
                ...lastItem,
                item: {
                    ...lastItem.item,
                    description: undefined
                },
                price: 0.0,
                quantity: undefined,
            }
        } else {
            const firstSelectable = invoiceDataStore.articles.filter(it => it.selectableByUser)[0];
            return {
                item: {
                    code: firstSelectable.code,
                    uomCode: firstSelectable.defaultUOMCode
                },
                price: 0.0,
                taxCode: firstSelectable.defaultVatCode,
            };
        }
    }

    private changeRow = (row: SalesInvoiceLineDto, changer: (l: SalesInvoiceLineDto) => SalesInvoiceLineDto) => {
        const lines = invoiceWizardState.invoiceLines;
        const index = lines.indexOf(row);
        const currentRow: SalesInvoiceLineDto | undefined = lines[index];
        if (!currentRow) {
            return;
        }
        lines[index] = changer(currentRow);
    }

    private changeArticle = (row: SalesInvoiceLineDto, code: string) => {
        const newArticle = invoiceDataStore.articles.find(it => it.code === code)!!;
        this.changeRow(row, (row) => ({
            ...row as any,
            item: {
                ...row.item,
                code: newArticle.code,
                uomCode: newArticle.defaultUOMCode
            },
            taxCode: newArticle.defaultVatCode,
        }))
    }

    private changeVat(row: SalesInvoiceLineDto, code: string) {
        this.changeRow(row, (row) => ({
            ...row as any,
            taxCode: code,
        }))
    }


    private changeUOM = (row: SalesInvoiceLineDto, code: any) => {
        // eslint-disable-next-line
        if (code == 0) {
            code = undefined;
        }

        this.changeRow(row, (row) => ({
            ...row as any,
            item: {
                ...row.item,
                uomCode: code
            },
        }))
    }

    private changeRowDescription(row: SalesInvoiceLineDto, text: string) {
        this.changeRow(row, row => ({
            ...row,
            // @ts-ignore
            item: {
                ...row.item,
                description: text
            }
        }))
    }

    private changeRowPrice(row: SalesInvoiceLineDto, number: any) {
        this.changeRow(row, row => ({
            ...row,
            price: number
        }))
    }

    private async fetchPdf() {
        const invoiceForPreview = this.assembleCreateInvoiceDto()
        if (!invoiceForPreview.invoiceNo) {
            invoiceForPreview.invoiceNo = this.state.expectedNextInvoiceNumber;
        }
        const base64 = await salesInvoiceApi.previewInvoice(invoiceForPreview);

        this.setState({
            pdfPreview: base64.data
        })
    }

    private changeRowDiscount(row: SalesInvoiceLineDto, text?: number) {
        if (text && text > 100) {
            text = 100;
        }
        this.changeRow(row, row => ({
            ...row,
            discountPct: text
        }))
    }

    private changeRowQuantity(row: SalesInvoiceLineDto, quantity?: number) {
        this.changeRow(row, row => ({
            ...row,
            quantity: quantity
        }))
    }


    private renderVat = (row: SalesInvoiceLineDto) => {
        return (
                <Select
                        style={{width: "100%", minWidth: "3.5rem"}}
                        value={row.taxCode}
                        onChange={(e) => this.changeVat(row, e.target.value as string)}>
                    {
                        Object.values(invoiceDataStore.taxes)
                                .filter(it => it.selectableByUser)
                                .map(it =>
                                        <MenuItem key={it.code} value={it.code}>
                                            {it.name}
                                        </MenuItem>
                                )
                    }
                </Select>
        )
    }

    private validateInvoiceNumberDelayed = throttle(async () => {
        const customInvoiceNumber = invoiceWizardState.customInvoiceNumber;
        if (customInvoiceNumber) {
            const customInvoiceValid = await salesInvoiceApi.checkInvoiceNumber(customInvoiceNumber);
            this.setState({
                customInvoiceValid: customInvoiceValid.canUse
            })
        } else {
            this.setState({customInvoiceValid: true})
        }
    }, 1000, {leading: false, trailing: true});

    private assembleCreateInvoiceDto = (): CreateSalesInvoiceDto => {
        const {selectedCustomerId, comment, invoiceLines} = invoiceWizardState;
        const customer = customersStore.find(selectedCustomerId)!!;
        let {invoiceDate, dueDate, selectedCurrency} = this.withCustomerDefaults(invoiceWizardState, customer);
        return {
            invoiceNo: invoiceWizardState.customInvoiceNumber,
            documentDate: invoiceDate.toDate(),
            dueDate: dueDate.toDate(),

            customerId: selectedCustomerId!!,
            currencyCode: selectedCurrency!!,
            comment: comment,
            invoiceLines: invoiceLines.map(it => {
                return {
                    ...it,
                    quantity: it.quantity ?? 1,
                    price: it.price ?? 0,
                }
            })
        }
    }
}

const ArticleSelector = ({row, changeArticle}: {
    row: SalesInvoiceLineDto,
    changeArticle: (row: SalesInvoiceLineDto, code: string) => void
}) => {
    const tOption = useTranslateCommon();
    const {t} = useTranslation();
    const selectableArticles = useMemo(() => Object.values(invoiceDataStore.articles).filter(it => it.selectableByUser), [invoiceDataStore.articles]);
    const selectedItem = useMemo(() => selectableArticles.find(it => it.code === row.item?.code), [selectableArticles, row]);
    const style = useMemo(() => {
        if (selectedItem === undefined) {
            return {minWidth: "18rem", color: "red"}
        } else {
            return {minWidth: "18rem"}
        }
    }, [selectedItem])
    return (
            <Select style={style} fullWidth={true} value={row.item?.code}
                    onChange={(e) => changeArticle(row, e.target.value as string)}
            >
                {selectableArticles.map(it =>
                        <MenuItem key={it.code} value={it.code}>{tOption("common.article", it.description)}</MenuItem>)
                }
                {selectedItem === undefined &&
                        <MenuItem value={row.item?.code} disabled={true}>
                            {t("common.article.select_correct_article", "Please select correct article")}
                        </MenuItem>
                }
            </Select>
    )
}

const UomSelect = ({row, changeUOM}: {
    row: SalesInvoiceLineDto,
    changeUOM: (row: SalesInvoiceLineDto, code: any) => void
}) => {
    const currentUom = row.item?.uomCode;
    const t = useTranslateCommon();
    return (
            <Select
                    style={{width: "100%", minWidth: "3.5rem"}}
                    value={currentUom ? currentUom : 0}
                    onChange={(e) => changeUOM(row, e.target.value)}>
                <MenuItem value={0}>
                    {t("common.uom", "N/A")}
                </MenuItem>
                {
                    Object.values(invoiceDataStore.uoms).map(it =>
                            <MenuItem key={it.code} value={it.code}>
                                {t("common.uom", it.name)}
                            </MenuItem>
                    )
                }
            </Select>
    )
}

export default withTranslation()(GenerateNewInvoiceWizard)